import type {
  AppointmentCommitTarget,
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewRow,
  AppointmentPlanningRouteProperty,
  AppointmentPlanningStrategy,
} from "../../../../../../api/kehrblatt/planning"
import { ceilPositiveMinutesToStep } from "../../../../../../_shared/_logic/time-granularity"
import { internalOrderTypeLabel } from "../../../../../../_shared/_logic/order-type-labels"
import type { AppointmentPlanningCalendarSource } from "../_shared/_logic/calendar-contract"
import type {
  AppointmentPlanningExistingSourceRow,
  AppointmentPlanningWorkOrderOverride,
} from "../../../_logic/appointments-contract"
import type { PlanningPreviewRow } from "../_shared/_logic/table-contract"

const clean = (value: unknown): string | null => {
  const text = String(value ?? "").trim()

  return text || null
}

const orderTypeLabelById = (orderTypes: OrderType[]): Map<string, string> =>
  new Map(
    orderTypes
      .map((type) => [String(type.id), clean(type.internal_label)] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[0] && entry[1])),
  )

const resolveOrderTypeLabel = (
  workOrder: AppointmentPlanningExistingSourceRow["workOrder"],
  labelsByOrderTypeId: Map<string, string>,
): string | null => {
  const direct = internalOrderTypeLabel(workOrder)
  if (direct) return direct

  const labels = (workOrder.order_types ?? [])
    .map((item) =>
      item.order_type_id ? labelsByOrderTypeId.get(String(item.order_type_id)) : null,
    )
    .filter((label): label is string => Boolean(label))

  return labels.length ? labels.join(", ") : null
}

const numericDuration = (value: unknown): number | null => ceilPositiveMinutesToStep(value)

const routePropertyById = (
  routeProperties: AppointmentPlanningRouteProperty[],
): Map<string, AppointmentPlanningRouteProperty> =>
  new Map(
    routeProperties
      .map((property) => [clean(property.property_id), property] as const)
      .filter((entry): entry is readonly [string, AppointmentPlanningRouteProperty] =>
        Boolean(entry[0]),
      ),
  )

const withRoutePropertyCoordinates = (
  property: Property | null,
  routePropertiesById: Map<string, AppointmentPlanningRouteProperty>,
): Property | null => {
  if (!property) return null

  const routeProperty = routePropertiesById.get(String(property.id ?? "").trim())

  if (!routeProperty) return property

  return {
    ...property,
    geo_lat: routeProperty.geo_lat ?? null,
    geo_lng: routeProperty.geo_lng ?? null,
  }
}

const hasAuthoritativeRouteCoordinates = (
  property: Property | null,
  routePropertiesById: Map<string, AppointmentPlanningRouteProperty>,
): boolean => Boolean(property && routePropertiesById.has(String(property.id ?? "").trim()))

export const buildAppointmentPlanningCalendarSources = ({
  planningExistingRows,
  orderTypes = [],
  routeProperties = [],
}: {
  planningExistingRows: AppointmentPlanningExistingSourceRow[]
  orderTypes?: OrderType[]
  routeProperties?: AppointmentPlanningRouteProperty[]
}): AppointmentPlanningCalendarSource[] => {
  const labelsByOrderTypeId = orderTypeLabelById(orderTypes)
  const routePropertiesById = routePropertyById(routeProperties)

  return planningExistingRows.map((row) => ({
    rowId: row.id,
    property: withRoutePropertyCoordinates(row.property, routePropertiesById),
    ...(hasAuthoritativeRouteCoordinates(row.property, routePropertiesById)
      ? { routeCoordinatesAuthoritative: true }
      : {}),
    workOrder: row.workOrder,
    orderType: resolveOrderTypeLabel(row.workOrder, labelsByOrderTypeId),
    dueDate: row.workOrder.due_date ?? null,
    estimatedDurationMin: numericDuration(row.workOrder.estimated_duration_min),
    unitId: row.workOrder.property_unit_id ? String(row.workOrder.property_unit_id) : null,
    unitLabel: row.workOrder.property_unit_label ?? null,
  }))
}

export const effectiveAppointmentForPlanningRow = ({
  row,
  baseRow = null,
  employeeLabelById,
}: {
  row: PlanningPreviewRow
  baseRow?: AppointmentPlanningPreviewRow | null
  employeeLabelById: Record<string, string>
}): AppointmentPlanningPreviewAppointment | null => {
  if (row.planningStrategy === "MANUAL") {
    if (
      !row.manualAppointment?.start_at ||
      !row.manualAppointment.end_at ||
      !row.manualAppointment.employee_id
    ) {
      return null
    }

    return {
      ...(baseRow?.appointment ?? {}),
      start_at: row.manualAppointment.start_at,
      end_at: row.manualAppointment.end_at,
      employee_id: row.manualAppointment.employee_id,
      employee_name: employeeLabelById[row.manualAppointment.employee_id] ?? null,
      flex_type: row.manualAppointment.flex_type,
      timeframe_start_at:
        row.manualAppointment.timeframe_start_at ?? row.manualAppointment.start_at,
      timeframe_end_at: row.manualAppointment.timeframe_end_at ?? row.manualAppointment.end_at,
      previous_anchor: baseRow?.appointment?.previous_anchor ?? null,
      next_anchor: baseRow?.appointment?.next_anchor ?? null,
    }
  }

  if (!row.appointmentStartAt || !row.appointmentEndAt || !row.appointmentEmployeeId) return null

  const flexType = row.appointmentTypeValue === "FLEX" ? "SOFT" : "HARD"

  return {
    ...(baseRow?.appointment ?? {}),
    start_at: row.appointmentStartAt,
    end_at: row.appointmentEndAt,
    employee_id: row.appointmentEmployeeId,
    employee_name: employeeLabelById[row.appointmentEmployeeId] ?? null,
    flex_type: flexType,
    timeframe_start_at:
      row.appointmentTimeframeStartAt ??
      baseRow?.appointment?.timeframe_start_at ??
      (flexType === "SOFT" ? row.appointmentStartAt : null),
    timeframe_end_at:
      row.appointmentTimeframeEndAt ??
      baseRow?.appointment?.timeframe_end_at ??
      (flexType === "SOFT" ? row.appointmentEndAt : null),
    previous_anchor: baseRow?.appointment?.previous_anchor ?? null,
    next_anchor: baseRow?.appointment?.next_anchor ?? null,
  }
}

// A complete visible draft is the write intent, not an invalidated server suggestion.
// Preserve the server's original event identity for optimistic concurrency.
export const commitTargetForPlanningRow = ({
  row,
  baseRow,
  appointment,
  sourceEventId,
}: {
  row: PlanningPreviewRow
  baseRow: AppointmentPlanningPreviewRow | null
  appointment: AppointmentPlanningPreviewAppointment | null
  sourceEventId?: string | null
}): AppointmentCommitTarget | null => {
  const sourceTarget = baseRow?.commit_target
  const expectedId =
    sourceTarget?.kind === "KEEP_EXISTING"
      ? sourceTarget.schedule_event_id
      : sourceTarget?.kind === "SET"
        ? sourceTarget.expected_active_schedule_event_id
        : sourceEventId

  if (row.planningStrategy === "KEEP_EXISTING") {
    return expectedId ? { kind: "KEEP_EXISTING", schedule_event_id: expectedId } : null
  }

  if (!appointment || !appointment.employee_id || !row.appointmentEmployeeId) return null

  if (row.allowedEmployeeIds?.length && !row.allowedEmployeeIds.includes(appointment.employee_id))
    return null

  const start = Date.parse(appointment.start_at)
  const end = Date.parse(appointment.end_at)
  const soft = appointment.flex_type === "SOFT"
  const timeframeStart = soft ? appointment.timeframe_start_at : appointment.start_at
  const timeframeEnd = soft ? appointment.timeframe_end_at : appointment.end_at
  const windowStart = Date.parse(timeframeStart ?? "")
  const windowEnd = Date.parse(timeframeEnd ?? "")

  if (
    ![start, end, windowStart, windowEnd].every(Number.isFinite) ||
    end <= start ||
    windowEnd <= windowStart ||
    start < windowStart ||
    end > windowEnd
  )
    return null

  const duration = Number(row.estimatedDurationValue)

  if (
    row.estimatedDurationValue &&
    (!Number.isFinite(duration) ||
      duration <= 0 ||
      (soft && duration * 60_000 > windowEnd - windowStart))
  )
    return null

  // Missing provenance is not evidence that an existing order has no appointment.
  if (expectedId === undefined) return null

  return {
    kind: "SET",
    expected_active_schedule_event_id: expectedId ?? null,
    appointment: {
      start_at: appointment.start_at,
      end_at: appointment.end_at,
      employee_id: appointment.employee_id,
      flex_type: soft ? "SOFT" : "HARD",
      timeframe_start_at: timeframeStart!,
      timeframe_end_at: timeframeEnd!,
    },
  }
}

export const buildEffectiveAppointmentPlanningPreviewRows = ({
  rows,
  previewByRowId,
  employeeLabelById,
  sourceEventIds,
}: {
  rows: PlanningPreviewRow[]
  previewByRowId: Map<string, AppointmentPlanningPreviewRow>
  employeeLabelById: Record<string, string>
  sourceEventIds?: ReadonlyMap<string, string | null>
}): AppointmentPlanningPreviewRow[] =>
  rows.map((row) => {
    const baseRow = previewByRowId.get(row.id) ?? null
    const appointment = effectiveAppointmentForPlanningRow({ row, baseRow, employeeLabelById })
    const availableStrategies =
      baseRow?.available_planning_strategies ??
      row.planningStrategyOptions
        .map((option) => option.value)
        .filter(
          (value): value is AppointmentPlanningStrategy =>
            value === "AUTO" || value === "MANUAL" || value === "KEEP_EXISTING",
        )

    return {
      commit_target: commitTargetForPlanningRow({
        row,
        baseRow,
        appointment,
        sourceEventId: sourceEventIds?.get(row.id),
      }),
      client_row_id: row.id,
      status: appointment ? "planned" : "unplanned",
      appointment,
      available_planning_strategies: availableStrategies,
      active_schedule_event: baseRow?.active_schedule_event ?? null,
      effective_planning_strategy: row.planningStrategy,
      manual_slot_options: [],
      resolved_appointment_contact: row.resolvedAppointmentContact ?? null,
      warnings:
        row.planningStrategy === "KEEP_EXISTING" &&
        baseRow?.effective_planning_strategy !== "KEEP_EXISTING"
          ? []
          : (baseRow?.warnings ?? []),
    }
  })
